/**
 * Masonry grid with append-safe columns.
 *
 * CSS multi-column balances every child across the columns, so appending a batch
 * reflows the whole grid and existing cards jump (measured: up to ~2800px). Here
 * each card is placed into a real column element and stays there, so a new batch
 * only ever adds to the bottom of the shortest column.
 */
class MasonryGrid extends HTMLElement {
  connectedCallback() {
    if (this.columns) return;
    this.columns = [];
    // Capture the server-rendered cards first: buildColumns() empties this element.
    const initial = Array.from(this.children).filter((el) =>
      el.classList.contains('masonry-card')
    );
    this.buildColumns();
    this.ingest(initial);

    // Resize only re-flows the existing order — reshuffling mid-resize would
    // rearrange the page under the reader.
    this.onResize = this.debounce(() => this.rebuild(), 150);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  debounce(fn, wait) {
    let t;
    return () => {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  /** Column count for the current breakpoint, read from the section's CSS vars. */
  get columnCount() {
    const styles = getComputedStyle(this);
    const read = (name, fallback) => {
      const value = parseInt(styles.getPropertyValue(name), 10);
      return Number.isFinite(value) && value > 0 ? value : fallback;
    };
    const width = window.innerWidth;
    if (width >= 990) return read('--masonry-columns', 4);
    if (width >= 750) return read('--masonry-columns-tablet', 2);
    return read('--masonry-columns-mobile', 1);
  }

  /** Every card, in their original order, wherever they currently live. */
  get cards() {
    if (!this.columns.length) return Array.from(this.children);
    const all = [];
    this.columns.forEach((column) => {
      Array.from(column.children).forEach((card, row) => all.push({ card, row }));
    });
    return all.sort((a, b) => a.row - b.row).map((entry) => entry.card);
  }

  buildColumns() {
    const count = this.columnCount;
    this.columns = [];
    this.replaceChildren();

    for (let i = 0; i < count; i++) {
      const column = document.createElement('div');
      column.className = 'masonry-column';
      this.appendChild(column);
      this.columns.push(column);
    }

    this.classList.add('masonry-products__grid--ready');
  }

  /** Re-flow the cards already on the page into a new column count. */
  rebuild() {
    if (this.columns.length === this.columnCount) return;
    const cards = this.cards;
    this.buildColumns();
    this.place(cards);
  }

  /**
   * Server order is deterministic — it has to be, because each batch is a
   * separate request and shuffling there would render the same block twice.
   * The randomness lives here instead: incoming special cards are shuffled and
   * dropped back among the products at jittered intervals, so which kind (if
   * any) lands in the first row changes on every load.
   */
  ingest(cards) {
    this.place(this.scatter(cards));
  }

  scatter(cards) {
    const specials = cards.filter((card) => MasonryGrid.typeOf(card));
    const products = cards.filter((card) => !MasonryGrid.typeOf(card));
    if (!specials.length || !products.length) return cards;

    for (let i = specials.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [specials[i], specials[j]] = [specials[j], specials[i]];
    }

    // Insertion points drawn uniformly across the run, then sorted. Strict even
    // segments made the first special land in row one every single time; uniform
    // draws mean the opening row is sometimes products only, sometimes a review,
    // sometimes an image or a bestseller.
    const points = specials
      .map(() => Math.floor(Math.random() * (products.length + 1)))
      .sort((a, b) => a - b);

    const out = [];
    let next = 0;
    for (let i = 0; i <= products.length; i++) {
      while (next < points.length && points[next] === i) out.push(specials[next++]);
      if (i < products.length) out.push(products[i]);
    }
    while (next < specials.length) out.push(specials[next++]);
    return out;
  }

  /** review / bestseller / image, or null for a plain product card. */
  static typeOf(card) {
    if (!card || !card.classList) return null;
    if (card.classList.contains('masonry-card--review')) return 'review';
    if (card.classList.contains('masonry-card--bestseller')) return 'bestseller';
    if (card.classList.contains('masonry-card--image')) return 'image';
    if (card.classList.contains('masonry-card--video')) return 'video';
    return null;
  }

  shortestOf(columns) {
    return columns.reduce((shortest, column) =>
      column.offsetHeight < shortest.offsetHeight ? column : shortest
    );
  }

  /**
   * Shortest column, but never one already ending in a card of the same kind —
   * otherwise reviews stack on reviews and the column reads as a wall of text.
   * Falls back to the plain shortest column when every column ends that way.
   */
  columnFor(card) {
    const type = MasonryGrid.typeOf(card);
    if (!type) return this.shortestOf(this.columns);

    const free = this.columns.filter(
      (column) => MasonryGrid.typeOf(column.lastElementChild) !== type
    );
    return this.shortestOf(free.length ? free : this.columns);
  }

  place(cards) {
    cards.forEach((card) => {
      if (!this.columns.length) {
        this.appendChild(card);
        return;
      }
      this.columnFor(card).appendChild(card);
    });
  }
}

class MasonryLoadMore extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.masonry-products');
    this.grid = this.section && this.section.querySelector('masonry-grid');
    if (!this.grid || !this.dataset.nextUrl) return;
    this.loading = false;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  buildSkeletons(count) {
    const cards = [];
    for (let i = 0; i < count; i++) {
      const card = document.createElement('div');
      card.className = 'masonry-card masonry-card--skeleton';
      card.setAttribute('aria-hidden', 'true');
      card.innerHTML =
        '<div class="masonry-skeleton__media"></div>' +
        '<div class="masonry-skeleton__line"></div>' +
        '<div class="masonry-skeleton__line masonry-skeleton__line--short"></div>';
      cards.push(card);
    }
    return cards;
  }

  async load() {
    if (this.loading) return;
    this.loading = true;
    this.observer.disconnect();

    const count = Math.max(1, parseInt(this.dataset.skeletons, 10) || 6);
    const skeletons = this.buildSkeletons(count);
    this.grid.place(skeletons);

    try {
      const response = await fetch(this.dataset.nextUrl);
      if (!response.ok) throw new Error(response.status);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = doc.querySelector('masonry-grid');
      const nextMore = doc.querySelector('masonry-load-more');

      skeletons.forEach((card) => card.remove());

      if (nextGrid) this.grid.ingest(Array.from(nextGrid.children));

      if (nextMore && nextMore.dataset.nextUrl) {
        this.dataset.nextUrl = nextMore.dataset.nextUrl;
        this.dataset.skeletons = nextMore.dataset.skeletons || this.dataset.skeletons;
        this.loading = false;
        this.observer.observe(this);
      } else {
        this.remove();
      }
    } catch (error) {
      skeletons.forEach((card) => card.remove());
      this.loading = false;
      this.observer.observe(this);
    }
  }
}

if (!customElements.get('masonry-grid')) customElements.define('masonry-grid', MasonryGrid);
if (!customElements.get('masonry-load-more')) customElements.define('masonry-load-more', MasonryLoadMore);
